import api from './api';

/**
 * 방문자 행동 추적 — 홈페이지에 처음 들어온 순간 브라우저에 UUID 하나(visitorId)를 만들어 두고,
 * 수업 전 단계의 행동(랜딩 페이지 조회, 커리큘럼 클릭, 도입 문의, 로그인·회원가입 등)을 같은 ID로 서버에 남긴다.
 * 관리자 > 방문자 분석에서 일일 방문자 수와 한 사람의 행동 흐름을 본다.
 *
 * - 수업이 시작된 뒤의 화면(멘토 포털, 학생 워크스페이스 등)은 추적하지 않는다. 페이지 조회는
 *   TRACKED_PATHS 의 공개 화면에서만 남기고, 그 밖의 이벤트도 공개 화면의 버튼에서만 부른다.
 * - 전송 실패는 조용히 무시한다(추적 때문에 화면 동작이 막히면 안 된다).
 * - localStorage 를 못 쓰는 환경(사생활 보호 모드 등)에서는 탭 단위 임시 ID 로 대신한다.
 */
const VISITOR_KEY = 'duo_visitor_id';
const SESSION_KEY = 'duo_visit_session';
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30분 동안 아무 행동이 없으면 새 방문(세션)으로 본다

// 페이지 조회를 남기는 공개 화면 — 접두사 일치
const TRACKED_PATHS = ['/main', '/curriculum/', '/inquiry', '/login', '/signup', '/instructor-apply', '/privacy', '/terms'];

let memoryVisitorId = null;

const uuid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    // randomUUID 미지원 환경용 RFC4122 v4 (CSPRNG 사용)
    if (window.crypto?.getRandomValues) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
        bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    // crypto API 자체가 없는 매우 구형 환경
    throw new Error('Secure random generator is not available in this browser.');
};

export const getVisitorId = () => {
    try {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id) {
            id = uuid();
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch {
        if (!memoryVisitorId) memoryVisitorId = uuid();
        return memoryVisitorId;
    }
};

// 같은 방문 안의 행동을 묶는 세션 ID — 마지막 행동 후 30분이 지나면 새로 만든다.
const getSessionId = () => {
    const now = Date.now();
    try {
        const saved = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
        const session = saved && now - saved.at < SESSION_IDLE_MS ? saved : { id: uuid() };
        localStorage.setItem(SESSION_KEY, JSON.stringify({ id: session.id, at: now }));
        return session.id;
    } catch {
        return null;
    }
};

export const isTrackedPath = (pathname) => TRACKED_PATHS.some((p) => pathname === p || pathname.startsWith(p));

const utmFromLocation = () => {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v.slice(0, 100);
    });
    return utm;
};

/**
 * 이벤트 하나를 보낸다. type 은 서버 화이트리스트(server/models/VisitorEvent.js EVENT_TYPES)에 있어야 저장된다.
 * @param {string} type
 * @param {Object} [props] - 짧은 문자열/숫자 값만 (예: { curriculum: 'AI 크리에이터', from: 'card' })
 */
export const trackEvent = (type, props = {}) => {
    try {
        const body = {
            visitorId: getVisitorId(),
            sessionId: getSessionId(),
            type,
            path: window.location.pathname,
            props,
            referrer: document.referrer || undefined,
            ...utmFromLocation()
        };
        const url = `${api.defaults.baseURL}/visitor-events`;
        // keepalive — 버튼을 누르고 곧바로 페이지가 바뀌어도 요청이 끝까지 간다.
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            keepalive: true,
            credentials: 'omit'
        }).catch(() => {});
    } catch {
        // 추적 실패는 무시
    }
};

export const trackPageView = (pathname) => {
    if (isTrackedPath(pathname)) trackEvent('page_view');
};
